import type { PortalCase } from '../lib/api.ts';
import { formatDate, humanize, statusTone } from '../lib/format.ts';
import { Badge, EmptyState } from './ui.tsx';

interface Props {
  cases: PortalCase[];
  selected: string | null;
  onSelect: (caseNumber: string) => void;
}

/**
 * The client's own case-status list (read-only). Each row is a selection button —
 * case number (mono), title, client-safe status badge and the last update; choosing a
 * row opens its read-only detail. Navigation only; there are no edit affordances.
 */
export function CaseList({ cases, selected, onSelect }: Props): JSX.Element {
  if (cases.length === 0) {
    return (
      <EmptyState
        compact
        title="No cases yet"
        description="When your firm shares a case with you, it appears here."
      />
    );
  }
  return (
    <ul className="portal-case-rows" aria-label="Your cases">
      {cases.map((c) => (
        <li key={c.case_number}>
          <button
            type="button"
            className="portal-case-row"
            aria-current={c.case_number === selected}
            onClick={() => onSelect(c.case_number)}
          >
            <span className="portal-case-row__number">{c.case_number}</span>
            <span className="portal-case-row__status">
              <Badge tone={statusTone(c.status)}>{humanize(c.status)}</Badge>
            </span>
            <span className="portal-case-row__title">{c.title}</span>
            <span className="portal-case-row__meta">
              {humanize(c.engagement_type)} · Updated {formatDate(c.updated_at)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
